import React, { useState } from "react";
import { Line, Radar } from "react-chartjs-2";
import CustomDropdown from "../common/CustomDropdown";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  RadialLinearScale,
} from "chart.js";
import CircularProgress from "./CircularProgress";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  RadialLinearScale,
  Title,
  Tooltip,
  Legend
);

const StaffStatistics = () => {
  const [selectedYear, setSelectedYear] = useState("2023");
  const [selectedMonth, setSelectedMonth] = useState("this_month");
  // Mock data for the win/loss record
  const mockData = {
    labels: [
      "JAN",
      "FEB",
      "MAR",
      "APR",
      "MAY",
      "JUN",
      "JUL",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ],
    wins: [2.5, 3, 2.5, 2, 4, 3.5, 4, 4, 4, 4.5, 3.5, 4.5],
    losses: [1, 2, 1, 1, 1, 0.5, 1, 1.5, 1, 1, 2, 1],
  };

  const data = {
    labels: mockData.labels,
    datasets: [
      {
        label: "Wins",
        data: mockData.wins,
        borderColor: "#10B981",
        backgroundColor: "#10B981",
        tension: 0.4,
        pointRadius: 0,
      },
      {
        label: "Losses",
        data: mockData.losses,
        borderColor: "#EF4444",
        backgroundColor: "#EF4444",
        tension: 0.4,
        pointRadius: 0,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        align: "start",
        labels: {
          boxWidth: 8,
          usePointStyle: true,
          pointStyle: "circle",
          padding: 20,
          color: "#000000",
        },
      },
      tooltip: {
        mode: "index",
        intersect: false,
        backgroundColor: "#000000",
        padding: 12,
        bodySpacing: 4,
        bodyFont: {
          size: 12,
        },
        titleFont: {
          size: 14,
          weight: "bold",
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: "#9CA3AF",
        },
      },
      y: {
        min: 0,
        max: 6,
        ticks: {
          stepSize: 1,
          color: "#9CA3AF",
        },
        grid: {
          color: "#E5E7EB",
          drawBorder: false,
        },
      },
    },
    interaction: {
      intersect: false,
      mode: "index",
    },
  };

  // Constants for goal stats
  const goalsScored = 54;
  const goalsConceded = 23;

  // Team Style data
  const teamStyleData = {
    labels: [
      "Defending",
      "Physical",
      "Passing",
      "Vision",
      "Attacking",
      "Dribbling",
      "Heading",
    ],
    datasets: [
      {
        data: [4, 3.5, 4, 4.5, 3.5, 3, 3.5],
        backgroundColor: "rgba(16, 185, 129, 0.2)",
        borderColor: "#10B981",
        borderWidth: 2,
        pointRadius: 0,
        fill: true,
      },
    ],
  };

  const teamStyleOptions = {
    scales: {
      r: {
        min: 0,
        max: 5,
        ticks: {
          stepSize: 1,
          display: false,
        },
        grid: {
          color: "#E5E7EB",
        },
        pointLabels: {
          font: {
            size: 12,
          },
          color: "#374151",
        },
        angleLines: {
          color: "#E5E7EB",
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
    },
  };

  return (
    <div className="space-y-8">
      {/* COACHING Section */}
      <div className="bg-white  rounded-xl">
        <h2 className="text-[#222A35] text-base font-bold mb-4">COACHING</h2>
        <div className="bg-white py-4 rounded-lg">
          <div className="flex gap-4 bg-[#F8F9FB] items-center  p-4 rounded-lg border border-[#D6DBEA]">
            <h3 className="text-[#222A35] text-base font-bold mb-4 w-[10%]">
              Top Formation
            </h3>
            <div className="bg-white rounded-lg px-4 py-3 border border-gray-200 w-[45%]">
              <div className="text-[#222A35] text-base font-bold">4-4-3</div>
              <div className="text-[#646B7E] text-sm">53 matches</div>
            </div>
            <div className="bg-white rounded-lg px-4 py-3 border border-gray-200 w-[45%]">
              <div className="text-[#222A35] text-base font-bold">4-3-4</div>
              <div className="text-[#646B7E] text-sm">53 matches</div>
            </div>
          </div>
        </div>
      </div>
      <h2 className="text-[#222A35] text-base font-bold mb-4">STATISTICS</h2>
      {/* Win/Loss Record Chart */}
      <div className="bg-white p-6 rounded-xl border border-[#D6DBEA]">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-[#222A35] text-base font-bold">
            Win/Loss record
          </h2>
          <CustomDropdown
            options={[
              { label: "2023", value: "2023" },
              { label: "2022", value: "2022" },
              { label: "2021", value: "2021" },
            ]}
            value={selectedYear}
            onChange={setSelectedYear}
            className="min-w-[100px]"
          />
        </div>
        <div
          className="bg-white p-6 rounded-lg border border-gray-100"
          style={{ height: "400px" }}
        >
          <Line data={data} options={options} />
        </div>
      </div>

      {/* Team Stats Row */}
      <div className="grid grid-cols-2 gap-6">
        {/* Team Goal Stats */}
        <div className="bg-white p-6 rounded-xl border border-[#D6DBEA]">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-[24px] font-bold text-black">
              Team goal stats
            </h2>
            <div className="relative">
              <CustomDropdown
                options={[
                  { label: "This month", value: "this_month" },
                  { label: "Last month", value: "last_month" },
                ]}
                value={selectedMonth}
                onChange={setSelectedMonth}
                className="min-w-[140px]"
              />
            </div>
          </div>
          <div className="flex items-center w-full">
            <div className="relative w-[200px] m-10 h-[200px] flex items-center justify-center">
              {/* Goals scored circle (outer) */}
              <div className="absolute inset-0">
                <CircularProgress
                  value={54}
                  maxValue={100}
                  color="#6EC03C"
                  size={200}
                  strokeWidth={16}
                  rotation={-90}
                />
              </div>

              {/* Goals conceded circle (inner) */}
              <div
                className="absolute ml-4"
                style={{ transform: "scale(0.7)" }}
              >
                <CircularProgress
                  value={23}
                  maxValue={100}
                  color="#D108A5"
                  size={200}
                  strokeWidth={16}
                  rotation={-90}
                />
              </div>
            </div>

            <div className="ml-20 flex flex-col gap-6 w-1/2">
              <div className="flex items-center gap-3">
                <div className="flex flex-col">
                  <span className="text-[#979699] text-[13px] flex gap-2 items-center">
                    <div className="w-3 h-3 rounded-full bg-[#10B981]"></div>
                    Goals scored
                  </span>
                  <span className="text-[17px] mt-3 font-bold text-[#23005B]">
                    54
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex flex-col">
                  <span className="text-[#979699] text-[13px] flex gap-2 items-center">
                    <div className="w-3 h-3 rounded-full bg-[#DB44A8]"></div>
                    Goals conceded
                  </span>
                  <span className="text-[17px] mt-3 font-bold text-[#23005B]">
                    23
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Team Style */}
        <div className="bg-white p-6 rounded-xl border border-[#D6DBEA]">
          <h2 className="text-[#222A35] text-base font-bold mb-4">
            Team style
          </h2>
          <div style={{ height: "300px" }}>
            <Radar data={teamStyleData} options={teamStyleOptions} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default StaffStatistics;
